/**
 * Cross-section links into the calendar.
 *
 * Other feature folders import these rather than composing path strings, the
 * same arrangement as `sessions/links.ts`: a route change here is a compiler
 * error there rather than a 404 the desk finds first.
 */

/**
 * The day view anchored on `at` — an ISO instant, which is what
 * `useCalendarUrlState` reads from `?date=` and resolves to a Tehran day.
 */
export function calendarDayRoute(at: string | Date): string {
  const iso = typeof at === 'string' ? at : at.toISOString();
  return `/calendar?date=${encodeURIComponent(iso)}&view=day`;
}

/**
 * One appointment, with its drawer open. The calendar resolves the day itself
 * (from the board when it is in range, otherwise with one lookup), so callers
 * need not know when the appointment is — a confirmed online request, say,
 * carries only the id.
 */
export function appointmentRoute(appointmentId: string, at?: string | Date): string {
  const params = new URLSearchParams({ appointment: appointmentId, view: 'day' });
  if (at) params.set('date', typeof at === 'string' ? at : at.toISOString());
  return `/calendar?${params.toString()}`;
}

/**
 * The booking form, opened pre-filled — for a «ثبت نوبت» button on the
 * patient file, a converted lead, or a service's page. The form opens on
 * today with the patient and/or the service already chosen.
 *
 * `procedureId` may be the global `Procedure.id` the scheduling API speaks or
 * a tariff row's own `ClinicProcedure.id`; the calendar resolves either
 * against the clinic's enabled services and drops one it does not offer,
 * rather than showing a raw id in the service list.
 *
 * The calendar still reads the older `?patient=` and `?book=` spellings, so
 * links already shipped keep working.
 */
export function newAppointmentRoute(
  options: { patientId?: string | null; procedureId?: string | null } = {},
): string {
  const params = new URLSearchParams({ new: '1' });
  if (options.patientId) params.set('patientId', options.patientId);
  if (options.procedureId) params.set('procedureId', options.procedureId);
  return `/calendar?${params.toString()}`;
}

/** {@link newAppointmentRoute} for one patient — the common case. */
export function newAppointmentForPatientRoute(
  patientId: string,
  options: { procedureId?: string | null } = {},
): string {
  return newAppointmentRoute({ patientId, procedureId: options.procedureId });
}
